import * as React from 'react';
import {useHistory, useParams, useLocation} from 'react-router-dom';
import {FormRef, GroupItem, RequiredRule, SimpleItem} from "devextreme-react/form";
import {Button, FileUploader, Form, Tabs, TextBox} from "devextreme-react";
import notify from "devextreme/ui/notify";
import {
    AttachmentTDO,
    CommentTDO, MaintenanceDTO,
    SupportTicketDTO, SupportTicketStatusDTO,
} from "../../Models/ApiModels";
import {ValueChangedEvent} from "devextreme/ui/file_uploader";
import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {AppStoreContext} from "../../Stores/AppStore";
import {observer} from "mobx-react";
import styles from "./Service.module.scss";
import {TicketChat} from "./TicketChat";
import {MessageEnteredEvent} from "devextreme/ui/chat";
import {Drawer} from "../Drawer/Drawer";
import {MAX_FILE_SIZE_BYTES, ALLOW_FILE_TYPES, AppRouting, CLASSIFICATION, TEXTAREA_MAX_LENGTH} from "../../AppConstants";
import {ChatRef} from "devextreme-react/chat";
import {authService} from "../../index";
import {renderWarningIcon} from "./Tickets";

enum TicketTabs {
    Info = 'Info',
    Comments = 'Comments'
}

const TicketDetails = observer(() => {
    const context = useContext(AppStoreContext);
    const history = useHistory();
    const params: { id: string } = useParams();
    const _chatRef = useRef<ChatRef>(null);
    const location = useLocation<{ task: MaintenanceDTO }>();
    const [ticket, setTicket] = useState<SupportTicketDTO | null>(null);
    const [tab, setTab] = useState<TicketTabs>(TicketTabs.Info);
    const [deletedFiles, setDeletedFiles] = useState<string[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const _formRef = useRef<FormRef>(null);
    const [comments, setComments] = React.useState<CommentTDO[]>([]);
    const {tickets, attachmentsData, filtersStore, loadTickets, loadComponents} = context;
    const { isGlobalSupport, isGlobalAdmin } = authService;

    useEffect(() => {
        void loadTickets();
    }, [filtersStore.selected.customer, loadTickets]);

    useEffect(() => {
        if(ticket && ticket?.deviceId){
            void context.loadTicketUsers(ticket?.deviceId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticket?.deviceId, context.loadTicketUsers])

    useEffect(() => {
        let edited = tickets?.find(t => t.id === params.id);
        const task = (location.state && location.state.task) || null;
        if (!edited) {
            edited = {} as SupportTicketDTO;
            if(task){
                edited.deviceId = task.deviceId;
                edited.description = task.description;
                edited.taskId = task.taskId;
            } else {
                edited.deviceId = filtersStore.selected.plant;
            }
        }
        else{
            edited.userId = edited?.user?.id; // || edited?.user?.displayName;
        }
        edited.componentId = edited.componentIds && edited.componentIds.length ? edited.componentIds[0] : '';
        setTicket(edited);
        void loadComponents(edited.deviceId);
    }, [params.id, tickets, filtersStore.selected.plant, loadComponents, location.state]);

    const getComments = useCallback(async (id: string) => {
        if (id) {
            const comments = await context.loadChatData(id);
            setComments(comments.filter(item => !!item.content || !!item.attachments.length));
            setTimeout(()=>{
                const chat = _chatRef.current?.instance();
                if(chat){
                    const scrollView = (chat as any)._messageList._scrollView;
                    if(scrollView){
                        scrollView.scrollTo(10000000)
                    }
                }
            }, 10)
        }
    }, [context]);


    useEffect(() => {
        setSelectedFiles([]);
        setDeletedFiles([]);
        if (ticket?.id) {
            void getComments(ticket?.id);
        } else {
            setComments([]);
        }

    }, [ticket, getComments]);

    const _onDeleteAttach = useCallback((id: string) => () => {
        const isDeleted = deletedFiles.includes(id);
        setDeletedFiles(isDeleted ? deletedFiles.filter(f => f !== id) : [...deletedFiles, id]);
    }, [deletedFiles]);
    const _removeFile = (e: File) => () => {
        setSelectedFiles(selectedFiles.filter(f => f !== e));
    };

    const _onSelectedFilesChanged = useCallback((e: ValueChangedEvent) => {
        if (e.value) {
            const newFileList = e.value.filter(f => {
                return !!ALLOW_FILE_TYPES.find(type => f.name.endsWith(type)) && f.size <= MAX_FILE_SIZE_BYTES;
            });

            const oversized =  e.value.filter(f => f.size > MAX_FILE_SIZE_BYTES);

            if (oversized.length > 0) {
                notify(
                    oversized.map(f => `File "${f.name}" is too large. Maximum allowed size is 5 MB.`).join(", "),
                    "error",
                    3000
                );
            }

            const newFileNames = newFileList.map(f => f.name);
            setSelectedFiles([...selectedFiles.filter(f => !newFileNames.includes(f.name)), ...newFileList]);
        }
    }, [selectedFiles, setSelectedFiles]);

    const addFileToComment = useCallback(async (file: File) => {
        if (ticket?.id && file) {
            const resp = await context.saveMessage(ticket.id, '');
            const commentId = ((resp as any).headers.location).split('/').reverse()[0];
            if (commentId) {
                await context.saveCommentFile(ticket?.id, commentId, file);
                await getComments(ticket?.id);
            }
        }
    }, [ticket?.id, getComments, context]);

    const saveComment = useCallback(async ({message}: MessageEnteredEvent) => {
        if (ticket?.id && message) {
            await context.saveMessage(ticket.id, message.text);
            void getComments(ticket?.id);
        }
    }, [ticket?.id, context, getComments]);

    const _onClose = useCallback(async () => {
        setTicket(null);
        history.push(AppRouting.Tickets);
    }, [history]);

    const _onSave = useCallback(async () => {
        const validateRes = _formRef.current?.instance().validate();
        if (validateRes?.isValid && ticket) {
            try {
                await context.saveTicket(ticket, deletedFiles, selectedFiles, comments);
                void _onClose();
            } catch (e) {

            }
        }
    }, [ticket, _onClose, context, selectedFiles, deletedFiles, comments]);

    const _closeTicket = useCallback(() => {
        context.confirmationStore.showConfirmation('Do you want to mark ticket as CLOSED?', 'Close ticket').then(() => {
            if (ticket) {
                ticket.status = SupportTicketStatusDTO.MarkedForClosing;
                setTicket(ticket);
                void _onSave();
            }
        });
    }, [ticket, context.confirmationStore, _onSave]);

    const isGlobalRole = isGlobalAdmin || isGlobalSupport;

    const isTicketEditingDisabled = React.useMemo(() => {
        return Boolean(ticket?.isResolved) || isGlobalRole
    }, [ticket, isGlobalRole])

    const closeTicketAction = (
        <Button
            type="default"
            text="Close Ticket"
            disabled={isTicketEditingDisabled}
            className={styles.mlAuto}
            onClick={_closeTicket}
        />
    );

    const onSelectionChanged = useCallback((args) => {
        const tab: TicketTabs = args.selectedItem || args.addedItems[0];
        setTab(tab);
    }, [setTab]);

    const _saveFile = useCallback((attachment:AttachmentTDO) => () =>{
        const src = attachmentsData.get(attachment.id);
        if(src){
            const link = document.createElement('a');
            link.href = src;
            link.setAttribute('download', attachment.name);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(src);
        }
        return false;
    }, [attachmentsData]);



    const isMarkedForClosing = ticket?.status === SupportTicketStatusDTO.MarkedForClosing && !Boolean(ticket?.isResolved);
    const isClosed = isMarkedForClosing || Boolean(ticket?.isResolved);

    const isUserExist = React.useMemo(() => {
        return Boolean(context.ticketsUsers.find(us => us.userId === ticket?.userId))
    }, [context.ticketsUsers, ticket]);

    const ticketUsers = React.useMemo(() => {
        const userDuplicate = context.ticketsUsers?.find(it => it.userId === ticket?.user?.id)? {...ticket?.user, userId: ticket?.user?.id} : undefined;
        return !isUserExist && ticket?.userId ? (
            userDuplicate ?
                context.ticketsUsers.map(us => {
                    if (userDuplicate?.userId === us.userId) {
                        return {...userDuplicate};
                    } else {
                        return {...us}
                    }
                }) :
                (ticket?.id ? [...context.ticketsUsers, {
                    id: ticket?.user?.id,
                    userId: ticket?.user?.id,
                    displayName: ticket?.user?.displayName
                }] : context.ticketsUsers)) : context.ticketsUsers;

    }, [context.ticketsUsers, ticket, isUserExist])

    return (
        <Drawer
            title={`${ticket?.id ? "Edit" : "New"} Ticket`}
            description={ticket?.id && renderWarningIcon(ticket || null)}
            onClose={_onClose}
            saveAction={isClosed || !ticket?.deviceId ? undefined : {
                onClick: _onSave,
                disabled: isTicketEditingDisabled,
                text: ticket?.id ? 'Save Ticket' : 'Create Ticket',
            }}
            closeAction={{
                onClick: _onClose,
                text: 'Cancel',
            }}
            titleAction={ticket?.id && !isClosed ? closeTicketAction : undefined}
        >

            <Tabs
                visible={!!ticket?.id}
                className={styles.tabs}
                dataSource={[TicketTabs.Info, TicketTabs.Comments]}
                scrollingEnabled={false}
                onSelectionChanged={onSelectionChanged}
                selectedItem={tab}
            />

            <Form
                formData={ticket}
                labelLocation="top"
                ref={_formRef}>

                <SimpleItem
                    dataField="title"
                    visible={tab !== TicketTabs.Comments}
                    editorOptions={{disabled: ticket?.id}}
                    label={{text: 'Ticket Title'}}>
                    <RequiredRule message="Title is required"/>
                </SimpleItem>

                <GroupItem cssClass="form-group" colCount={2} visible={tab !== TicketTabs.Comments}>
                    <GroupItem>
                        <SimpleItem
                            dataField="componentId"
                            editorType="dxSelectBox"
                            editorOptions={{
                                items: context.filtersStore.selected.plant ? context.components.get(context.filtersStore.selected.plant) : Array.from(context.components.values()).flat() || [],
                                displayExpr: "name",
                                valueExpr: "id",
                                disabled: ticket?.id
                            }}
                            label={{text: 'Component'}}
                        >
                            <RequiredRule message="Component is required"/>
                        </SimpleItem>
                        <SimpleItem
                            dataField="userId"
                            editorType="dxSelectBox"
                            editorOptions={{
                                items: ticketUsers,
                                disabled: isClosed || isTicketEditingDisabled,
                                displayExpr: "displayName",
                                valueExpr: "userId",
                                placeholder: ticket && ticket.user?.displayName && !ticket.user?.id ? ticket.user.displayName : "Select...",

                            }}
                            label={{text: 'User'}}
                        >
                            <RequiredRule message="User is required"/>
                        </SimpleItem>
                    </GroupItem>
                    <GroupItem>
                        <SimpleItem
                            label={{text: 'Plant'}}>
                            <TextBox
                                defaultValue={context.filtersStore.filters.plants.find(e => e.id === ticket?.deviceId)?.name}
                                disabled
                            />
                            <RequiredRule/>
                        </SimpleItem>
                        <SimpleItem
                            dataField="classification"
                            editorType="dxSelectBox"
                            editorOptions={{
                                items: CLASSIFICATION,
                                displayExpr: "name",
                                valueExpr: "id",
                                disabled: ticket?.id
                            }}
                            label={{text: 'Classification'}}
                        >
                            <RequiredRule message="Component is required"/>
                        </SimpleItem>
                    </GroupItem>
                    <GroupItem>
                        <SimpleItem
                            visible={!!ticket?.id}
                            label={{text: 'Priority'}}>
                            <TextBox
                                defaultValue={ticket?.priority}
                                disabled
                            />
                            <RequiredRule/>
                        </SimpleItem>
                    </GroupItem>
                </GroupItem>
                <GroupItem visible={tab !== TicketTabs.Comments}>
                    <SimpleItem
                        dataField="description"
                        colSpan={1}
                        editorType="dxTextArea"
                        editorOptions={{
                            maxLength: TEXTAREA_MAX_LENGTH,
                            height: 100,
                            valueChangeEvent: "input",
                            disabled: isClosed || isTicketEditingDisabled,
                            onInitialized: (e: any) => {
                                const editor = e.component;
                                const element = editor.element();
                                const counter = document.createElement("div");
                                counter.style.textAlign = "right";
                                counter.style.fontSize = "11px";
                                counter.style.marginTop = "4px";
                                counter.style.color = "#888";
                                counter.style.position = "absolute";
                                counter.style.bottom = "0px";
                                counter.style.right = "5px";
                                element.append(counter);

                                const value = editor.option("value") || "";
                                counter.innerText = `${value.length}/${TEXTAREA_MAX_LENGTH}`;

                                editor.__counter = counter;
                            },

                            onValueChanged: (e: any) => {
                                const counter = e.component.__counter;
                                if (counter) {
                                    counter.innerText = `${(e.value?.length ?? 0)}/${TEXTAREA_MAX_LENGTH}`;
                                }
                            }
                        }}
                        label={{text: 'Description'}}>
                        <RequiredRule message="Description is required"/>
                    </SimpleItem>
                    <SimpleItem
                        colSpan={1}
                        label={{text: 'Files'}}>
                        <div className={styles.fileWrapper}>
                            {comments.filter(c => c.attachments.length).map(({attachments: [e]}) => {
                                const isDeleted = deletedFiles.includes(e.id);
                                return (
                                    <div className={`${styles.item} ${isDeleted ? styles.deleted : ''}`}
                                         key={e.id}>
                                        <span hidden={isClosed || isGlobalRole} className={styles.icon} onClick={_onDeleteAttach(e.id)}>
                                            <i className={`dx-icon ${isDeleted ? 'dx-icon-undo' : 'dx-icon-trash'}`}></i>
                                        </span>
                                        <span className={styles.name} onClick={_saveFile(e)}>{e.name}</span>
                                    </div>
                                );
                            })}
                            {selectedFiles.map(e => {
                                return (
                                    <div className={styles.item} key={e.name}>
                                        <span className={styles.icon} onClick={_removeFile(e)}>
                                            <i className="dx-icon dx-icon-close"></i>
                                        </span>
                                        <span className={styles.name}>{e.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                        {!isClosed && !isGlobalRole &&
                            <FileUploader
                                selectButtonText="Select File"
                                className={styles.file}
                                multiple={true}
                                labelText=""
                                accept={ALLOW_FILE_TYPES.join(',')}
                                maxFileSize={MAX_FILE_SIZE_BYTES}
                                onValueChanged={_onSelectedFilesChanged}
                                showFileList={false}
                                uploadMode="useButtons"/>
                        }
                    </SimpleItem>
                </GroupItem>

                {!!ticket?.id && tab === TicketTabs.Comments &&
                    <GroupItem>
                        <SimpleItem
                            colSpan={1}
                            label={{text: 'Comments'}}>
                            <TicketChat
                                chatRef={_chatRef}
                                disabled={Boolean(ticket?.isResolved) || isGlobalRole}
                                ticketId={ticket.id}
                                addFile={addFileToComment}
                                comments={comments}
                                saveComment={saveComment}
                            />
                        </SimpleItem>
                    </GroupItem>
                }
            </Form>
        </Drawer>
    );
});
export default TicketDetails;